import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    AppBar,
    Box,
    Toolbar,
    IconButton,
    Typography,
    Menu,
    Container,
    Avatar,
    Button,
    Tooltip,
    MenuItem,
    Link,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { EmojiEvents } from '@mui/icons-material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import InstagramIcon from '@mui/icons-material/Instagram';
import { TikTokIcon } from './icons/TikTokIcon';
import { useThemeMode } from '../contexts/ThemeContext';

const Layout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, user, logout } = useAuth();
    const [anchorElNav, setAnchorElNav] = useState(null);
    const [anchorElUser, setAnchorElUser] = useState(null);

    const { mode, toggleTheme } = useThemeMode();

    const isInstagramLanding = location.pathname === '/Instagram' || location.pathname === '/instagram';
    const isEbookPage = location.pathname === '/ebook' || location.pathname === '/ebook/thank-you';
    const isLandingPage = isInstagramLanding || isEbookPage;

    // Navigation items based on authentication status
    const publicPages = [
        { title: 'About', path: '/about' },
        { title: 'Books', path: '/books' },
        { title: 'Affiliate Program', path: '/affiliate' },
        { title: 'Blog', path: '/blog' },
        { title: 'Features', path: '/features' },
        { title: 'Pricing', path: '/subscription' },
        { title: 'Speed Test', path: '/speed-test', highlight: true }
    ];

    const authenticatedPages = [
        { title: 'Achievements', path: '/achievements' },
        { title: 'Blog', path: '/blog' },
        { title: 'Books', path: '/books' },
        { title: 'Dashboard', path: '/dashboard' },
        { title: 'Leaderboard', path: '/leaderboards' },
        { title: 'Speed Reader', path: '/reader', highlight: true },
        { title: 'Vocabulary', path: '/vocabulary' }
    ];

    // Simplified nav for landing pages - fewer exits for cold traffic
    const landingPages = [
        { title: 'Blog', path: '/blog' },
        { title: 'Speed Test', path: '/speed-test', highlight: true }
    ];

    const navigationItems = isLandingPage
        ? landingPages
        : isAuthenticated
            ? authenticatedPages
            : publicPages;

    const handleOpenNavMenu = (event) => {
        setAnchorElNav(event.currentTarget);
    };

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <AppBar position="static">
                <Container maxWidth="xl">
                    <Toolbar disableGutters>
                        {/* Logo - Desktop */}
                        <Box
                            component={RouterLink}
                            to="/"
                            sx={{
                                mr: 3,
                                display: { xs: 'none', md: 'flex' },
                                alignItems: 'center',
                                textDecoration: 'none',
                                gap: 1.5,
                            }}
                        >
                            <img src="/icon.png" alt="ReaderFlex" title="ReaderFlex" style={{ height: 34 }} />
                            <Typography variant="h6" noWrap sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                                <Box component="span" sx={{ color: 'text.primary' }}>Reader</Box>
                                <Box component="span" sx={{ color: '#8DC63F' }}>FLEX</Box>
                            </Typography>
                        </Box>

                        {/* Mobile Menu */}
                        <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                            <IconButton
                                size="large"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleOpenNavMenu}
                                color="inherit"
                            >
                                <MenuIcon />
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorElNav}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                open={Boolean(anchorElNav)}
                                onClose={handleCloseNavMenu}
                                sx={{
                                    display: { xs: 'block', md: 'none' },
                                }}
                            >
                                {navigationItems.map((page) => (
                                    <MenuItem 
                                        key={page.title} 
                                        onClick={() => {
                                            handleCloseNavMenu();
                                            navigate(page.path);
                                        }}
                                    >
                                        <Typography textAlign="center">{page.title}</Typography>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>

                        {/* Logo - Mobile */}
                        <Box
                            component={RouterLink}
                            to="/"
                            sx={{
                                mr: 2,
                                display: { xs: 'flex', md: 'none' },
                                flexGrow: 1,
                                alignItems: 'center',
                                textDecoration: 'none',
                            }}
                        >
                            <img src="/icon.png" alt="ReaderFlex" title="ReaderFlex" style={{ height: 32 }} />
                        </Box>

                        {/* Desktop Menu */}
                        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                            {navigationItems.map((page) => (
                                <Button
                                    key={page.title}
                                    component={RouterLink}
                                    to={page.path}
                                    onClick={handleCloseNavMenu}
                                    sx={{
                                        my: 2,
                                        display: 'block',
                                        color: page.highlight ? 'secondary.main' : 'text.secondary',
                                        fontWeight: page.highlight ? 700 : undefined,
                                        '&:hover': { color: 'primary.main' }
                                    }}
                                >
                                    {page.title}
                                </Button>
                            ))}
                        </Box>

                        {/* Instagram + TikTok - hidden on landing pages */}
                        {!isLandingPage && (
                        <Tooltip title="Follow us on Instagram">
                            <IconButton
                                component="a"
                                href="https://instagram.com/tryreaderflex"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ mr: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                            >
                                <InstagramIcon />
                            </IconButton>
                        </Tooltip>
                        )}
                        {!isLandingPage && (
                        <Tooltip title="Follow us on TikTok">
                            <IconButton
                                component="a"
                                href="https://www.tiktok.com/@readerflex"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ mr: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                            >
                                <TikTokIcon />
                            </IconButton>
                        </Tooltip>
                        )}

                        {/* Theme Toggle - hidden on landing pages */}
                        {!isLandingPage && (
                        <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                            <IconButton onClick={toggleTheme} sx={{ mr: 1, color: 'text.secondary' }}>
                                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                            </IconButton>
                        </Tooltip>
                        )}

                        {/* Auth Buttons or User Menu */}
                        <Box sx={{ flexGrow: 0 }}>
                            {isAuthenticated ? (
                                <>
                                    <Tooltip title="Open settings">
                                        <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                            <Avatar 
                                                src={user?.profile?.avatarUrl} 
                                                alt={user?.profile?.displayName || user?.username}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    bgcolor: !user?.profile?.avatarUrl ? 'primary.main' : undefined
                                                }}
                                            >
                                                {(user?.profile?.displayName || user?.username || 'U')[0].toUpperCase()}
                                            </Avatar>
                                        </IconButton>
                                    </Tooltip>
                                    <Menu
                                        sx={{ mt: '45px' }}
                                        id="menu-appbar"
                                        anchorEl={anchorElUser}
                                        anchorOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        keepMounted
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        open={Boolean(anchorElUser)}
                                        onClose={handleCloseUserMenu}
                                    >
                                        <MenuItem onClick={() => {
                                            handleCloseUserMenu();
                                            navigate('/profile');
                                        }}>
                                            <Typography textAlign="center">Profile</Typography>
                                        </MenuItem>
                                        <MenuItem onClick={() => {
                                            handleCloseUserMenu();
                                            navigate('/email-preferences');
                                        }}>
                                            <Typography textAlign="center">Email Preferences</Typography>
                                        </MenuItem>
                                        <MenuItem onClick={() => {
                                            handleCloseUserMenu();
                                            navigate('/subscription');
                                        }}>
                                            <Typography textAlign="center">Subscription</Typography>
                                        </MenuItem>
                                        <MenuItem onClick={handleLogout}>
                                            <Typography textAlign="center">Logout</Typography>
                                        </MenuItem>
                                    </Menu>
                                </>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        component={RouterLink}
                                        to="/login"
                                        variant="outlined"
                                        color="primary"
                                    >
                                        Login
                                    </Button>
                                    <Button
                                        component={RouterLink}
                                        to="/register"
                                        variant="contained"
                                        color="secondary"
                                    >
                                        Sign Up
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            <Box component="main" sx={{ flexGrow: 1 }}>
                {children}
            </Box>

            {!isLandingPage && (
            <Box
                component="footer"
                sx={{
                    py: 3,
                    px: 2,
                    mt: 'auto',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#0A0F1A' : '#F0F2F5',
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                <Container maxWidth="lg">
                    <Typography variant="body2" color="text.secondary" align="center">
                        <Link color="inherit" component={RouterLink} to="/about">
                            About
                        </Link>
                        {' | '}
                        <Link color="inherit" component={RouterLink} to="/books">
                            Books
                        </Link>
                        {' | '}
                        <Link color="inherit" component={RouterLink} to="/legal/privacy">
                            Privacy Policy
                        </Link>
                        {' | '}
                        <Link color="inherit" component={RouterLink} to="/legal/terms">
                            Terms of Service
                        </Link>
                        {' | '}
                        <Link color="inherit" component={RouterLink} to="/affiliate">
                            Affiliate Program
                        </Link>
                        {' | '}
                        <Link color="inherit" component={RouterLink} to="/blog">
                            Blog
                        </Link>
                        {' | '}
                        <Link color="inherit" href="https://instagram.com/tryreaderflex" target="_blank" rel="noopener noreferrer">
                            Instagram
                        </Link>
                        {' | '}
                        <Link color="inherit" href="https://www.tiktok.com/@readerflex" target="_blank" rel="noopener noreferrer">
                            TikTok
                        </Link>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                        &copy; {new Date().getFullYear()} Emberline Ventures, LLC. All rights reserved.
                    </Typography>
                </Container>
            </Box>
            )}
        </Box>
    );
};

export default Layout;
